import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { logActivity } from '../utils/logActivity';
import { retrogradeStudyLabel } from '../utils/planetAbbreviations';
import { useAuth } from '../context/AuthContext';
import { withFrame } from '../utils/framePrefix';

import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Select, MenuItem, Box, Typography, TextField
} from '@mui/material';
import HelpIcon from './HelpIcon';
import { useChartOverlays } from '../context/ChartOverlayContext';

import { addAreaHighlightOverlaysAndMessages } from './addAreaHighlightOverlaysUtil';


const PLANETS = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu'];
const RETRO_PLANETS = PLANETS.filter(p => p !== 'Sun' && p !== 'Moon' && p !== 'Rahu');
const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
const NAKSHATRAS = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra',
  'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
  'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
  'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta',
  'Shatabhisha', 'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'
];
/**
 * Frames a study can be computed in.
 *
 * Two independent choices, so four modes. Where the observer stands — Earth (geo) or the Sun
 * (helio) — decides which bodies exist and whether retrograde motion is in play. Where the zodiac
 * starts — the equinox (tropical) or the fixed-star reference (sidereal, the Lahiri ayanamsha away
 * from it) — only relabels degrees; it moves no body.
 *
 * The names are the ones the backend resolves: ZodiacFrame.from() on the Java side.
 */
const ZODIAC_TYPES = ['geo-sidereal', 'geo-tropical', 'helio'];

/**
 * The fourth mode, offered to admins only.
 *
 * Heliocentric positions are well defined and sidereal boundaries are well defined, but the
 * combination has no established interpretation, so it stays out of the customer-facing list.
 * Hiding it is presentation, not enforcement — the backend returns 403 for a non-admin regardless
 * of what the dialog offered.
 */
const ADMIN_ONLY_ZODIAC_TYPES = ['helio-sidereal'];

/**
 * What each frame is called on screen.
 *
 * The wire values stay as they are — they are what the backend's ZodiacFrame.from() resolves and
 * what a saved study records — but "helio" and "geo-sidereal" say nothing to a reader about which
 * of the two choices they are making. The label spells out both: centre first, then where the
 * zodiac starts.
 */
const ZODIAC_LABELS = {
  'geo-sidereal': 'Geocentric Sidereal',
  'geo-tropical': 'Geocentric Tropical',
  'helio': 'Heliocentric Tropical',
  'helio-sidereal': 'Heliocentric Sidereal (Lahiri)',

  // The pre-rename spellings, which ZodiacFrame.from() still accepts on the backend. Nothing in
  // this dialog puts them in the menu — the list is built from ZODIAC_TYPES — but zodiacTypesFor()
  // adds whatever is currently SELECTED to the options so the Select never renders blank, and a
  // selection restored from an older saved study or a stored preference could still be one of
  // these. Mapping them means such a value reads as its frame rather than as a raw token.
  'sidereal': 'Geocentric Sidereal',
  'tropical': 'Geocentric Tropical',
  'heliocentric': 'Heliocentric Tropical',
};

/** The on-screen name for a frame, falling back to the wire value for anything unmapped. */
const zodiacLabel = (frame) => ZODIAC_LABELS[frame] || frame;

/**
 * The frames this user may pick from, always including whatever is currently selected.
 *
 * The second part matters during a token refresh. Auth briefly reports every user as a
 * non-admin, the list contracts to three, and a Select whose value is no longer among its options
 * renders BLANK — the same trap the planet list has to avoid when the frame turns heliocentric.
 * The selection itself is kept (see the reset effect, which waits for auth to settle), so dropping
 * it from the list would show an empty box for a frame that is still very much selected.
 *
 * This is presentation only. The backend answers 403 for a non-admin asking for helio-sidereal
 * however the option got on screen, and a user who really is not an admin has the selection reset
 * out from under this the moment auth resolves.
 */
const zodiacTypesFor = (isAdmin, selected) => {
  const frames = isAdmin ? [...ZODIAC_TYPES, ...ADMIN_ONLY_ZODIAC_TYPES] : [...ZODIAC_TYPES];
  if (selected && !frames.includes(selected)) frames.push(selected);
  return frames;
};

/**
 * Whether this user may be offered the admin-only frame.
 *
 * Deliberately more than the context's isAdmin flag. That flag is derived from
 * {@code user.subscriptionType} alone, and there is a reachable path where a genuine admin arrives
 * without it: AuthContext falls back to `{ uid, email, fallback: true }` whenever the
 * /api/user/me fetch fails with anything other than a 401 — a 404, a 500, a dropped connection.
 * The session then carries no subscriptionType at all, every admin-only control quietly
 * disappears for the rest of it, and nothing on screen says why. That is the most likely reason
 * for a fourth option that "should be there" not being there.
 *
 * So read every witness the payload actually carries. /api/user/me serialises the Java User with
 * Jackson and User.isAdmin() is a public getter, so the JSON holds `admin: true` alongside
 * `subscriptionType: "ADMIN"`; `role` is accepted too, for documents written by older paths.
 *
 * Unknown is NOT treated as admin. The frame is enforced at the endpoint with a 403, so guessing
 * generously here would replace a missing option with a failing request — worse, not better.
 */
const isAdminUser = (isAdminFlag, user) => {
  if (isAdminFlag) return true;
  if (!user) return false;
  if (user.admin === true) return true;
  const role = String(user.role || user.subscriptionType || '').toUpperCase();
  return role === 'ADMIN';
};

/** True for both frames centred on the Sun. */
const isHeliocentricFrame = (frame) => frame === 'helio' || frame === 'helio-sidereal';

/**
 * Bodies with no heliocentric position.
 *
 * The Sun is the origin of that frame. Rahu and Ketu are the lunar nodes — intersections of the
 * Moon's orbit around EARTH with the ecliptic — so they are undefined without Earth at the centre,
 * and the ephemeris fails outright rather than returning a poor answer. Asking for one is what
 * produced an HTTP 500 on the Longitude study.
 *
 * The Moon is deliberately NOT here: its heliocentric longitude tracks Earth's, which makes it the
 * usable proxy for Earth in that frame.
 */
const NO_HELIOCENTRIC_POSITION = ['Sun', 'Rahu', 'Ketu', 'North Node', 'South Node'];

/** Bodies selectable in a given frame. */
const planetsForFrame = (frame) => (isHeliocentricFrame(frame)
  ? PLANETS.filter(p => !NO_HELIOCENTRIC_POSITION.includes(p))
  : PLANETS);

/** Where a selection lands when the chosen body is unavailable in the new frame. */
const fallbackPlanet = (frame) => {
  const valid = planetsForFrame(frame);
  // Mercury rather than the list head, which is the Sun's slot and reads as an arbitrary jump.
  return valid.includes('Mercury') ? 'Mercury' : (valid[0] || '');
};
const CHARANS = [1, 2, 3, 4];


const OVERLAY_COLORS = [
  'rgba(229, 57, 53, 0.55)',
  'rgba(30, 136, 229, 0.55)',
  'rgba(67, 160, 71, 0.55)',
  'rgba(244, 81, 30, 0.55)',
  'rgba(142, 36, 170, 0.55)',
  'rgba(255, 195, 0, 0.55)',
  'rgba(255, 87, 51, 0.55)',
  'rgba(199, 0, 57, 0.55)',
  'rgba(144, 12, 63, 0.55)',
  'rgba(88, 24, 69, 0.55)',
  'rgba(218, 247, 166, 0.55)',
  'rgba(255, 195, 51, 0.55)',
];


const AreaHighlightDialog = ({ open, onClose, barData, extendedBarData, onRun, onJournalAppend }) => {
  const { user, api, isAdmin, loading: authLoading } = useAuth();
  const { addOverlay } = useChartOverlays();

  // Recomputed every render, so a profile that arrives after the dialog is already open adds the
  // fourth frame as soon as it lands rather than on the next time it is opened.
  const adminAllowed = isAdminUser(isAdmin, user);
  const [lastRunStudy, setLastRunStudy] = useState(null);
  const [color, setColor] = useState(OVERLAY_COLORS[0]);

  // Planet in Degree Range state
  const [planetFrom, setPlanetFrom] = useState(PLANETS[0]);
  const [fromDegree, setFromDegree] = useState('30');
  const [toDegree, setToDegree] = useState('60');
  const [zodiacType1, setZodiacType1] = useState(ZODIAC_TYPES[0]);

  // Planet in Sign state
  const [planetSignPlanet, setPlanetSignPlanet] = useState(PLANETS[0]);
  const [planetSignSign, setPlanetSignSign] = useState(SIGNS[0]);
  const [planetSignZodiac, setPlanetSignZodiac] = useState(ZODIAC_TYPES[0]);

  // Switching to a heliocentric frame can invalidate the current body, and a Select showing a
  // value that is no longer among its options renders blank rather than reporting the problem.
  useEffect(() => {
    if (NO_HELIOCENTRIC_POSITION.includes(planetFrom) && isHeliocentricFrame(zodiacType1)) {
      setPlanetFrom(fallbackPlanet(zodiacType1));
    }
  }, [zodiacType1, planetFrom]);

  useEffect(() => {
    if (NO_HELIOCENTRIC_POSITION.includes(planetSignPlanet) && isHeliocentricFrame(planetSignZodiac)) {
      setPlanetSignPlanet(fallbackPlanet(planetSignZodiac));
    }
  }, [planetSignZodiac, planetSignPlanet]);

  // The ONLY thing that may change a frame the user chose, and only in one case: a non-admin
  // holding helio-sidereal, which the endpoint would refuse with a 403 they could not act on.
  //
  // Nothing else resets the frame. A study used to put it back to geo-sidereal when it ran, which
  // made a deliberate choice look like it had been ignored — pick Heliocentric Tropical, press Run,
  // watch the box say Geocentric Sidereal — and left the next run on the default unless the user
  // noticed and re-picked. A frame now stays selected until it is changed.
  //
  // Nothing happens while auth is still resolving either. During that window every user looks like
  // a non-admin, and clearing the selection then would undo an admin's choice on a token refresh.
  useEffect(() => {
    if (authLoading || adminAllowed) return;
    if (ADMIN_ONLY_ZODIAC_TYPES.includes(zodiacType1)) setZodiacType1(ZODIAC_TYPES[0]);
    if (ADMIN_ONLY_ZODIAC_TYPES.includes(planetSignZodiac)) setPlanetSignZodiac(ZODIAC_TYPES[0]);
  }, [authLoading, adminAllowed, zodiacType1, planetSignZodiac]);

  // Says, in the console, exactly why the frame list is the length it is. An admin missing the
  // fourth option has several possible causes that look identical on screen — a profile fetch that
  // fell back, a subscriptionType that is not ADMIN, auth still loading — and this distinguishes
  // them without a debugger.
  useEffect(() => {
    if (!open) return;
    console.log('[AreaHighlightDialog] frame options', {
      frames: zodiacTypesFor(adminAllowed, null),
      adminAllowed,
      contextIsAdmin: isAdmin,
      authLoading,
      subscriptionType: user?.subscriptionType,
      adminField: user?.admin,
      role: user?.role,
      usingFallbackProfile: Boolean(user?.fallback),
    });
  }, [open, adminAllowed, isAdmin, authLoading, user]);

  // Auto-select color based on dropdown selection
  React.useEffect(() => {
    // For Planet in Sign: use planetSignSign index
    const idx = SIGNS.indexOf(planetSignSign);
    if (idx !== -1) {
      setColor(OVERLAY_COLORS[idx % OVERLAY_COLORS.length]);
    }
  }, [planetSignSign]);

  // Planet in Nakshatra state
  const [planetNakshatraPlanet, setPlanetNakshatraPlanet] = useState(PLANETS[0]);
  const [planetNakshatra, setPlanetNakshatra] = useState(NAKSHATRAS[0]);

  // For Planet in Nakshatra: use planetNakshatra index
  React.useEffect(() => {
    const idx = NAKSHATRAS.indexOf(planetNakshatra);
    if (idx !== -1) {
      setColor(OVERLAY_COLORS[idx % OVERLAY_COLORS.length]);
    }
  }, [planetNakshatra]);

  // Planet in Nakshatra Charan state
  const [planetCharanPlanet, setPlanetCharanPlanet] = useState(PLANETS[0]);
  const [planetCharanNakshatra, setPlanetCharanNakshatra] = useState(NAKSHATRAS[0]);
  const [planetCharanCharan, setPlanetCharanCharan] = useState(CHARANS[0]);

  // For Planet in Nakshatra Charan: use planetCharanNakshatra index
  React.useEffect(() => {
    const idx = NAKSHATRAS.indexOf(planetCharanNakshatra);
    if (idx !== -1) {
      setColor(OVERLAY_COLORS[idx % OVERLAY_COLORS.length]);
    }
  }, [planetCharanNakshatra]);

  // Retro/Direct state
  const [retroPlanet, setRetroPlanet] = useState(RETRO_PLANETS[0]);
  const [directPlanet, setDirectPlanet] = useState(RETRO_PLANETS[0]);

  // For Retrograde to Direct: use retroPlanet index
  React.useEffect(() => {
    const idx = RETRO_PLANETS.indexOf(retroPlanet);
    if (idx !== -1) {
      setColor(OVERLAY_COLORS[idx % OVERLAY_COLORS.length]);
    }
  }, [retroPlanet]);

  // For Direct to Retrograde: use directPlanet index
  React.useEffect(() => {
    const idx = RETRO_PLANETS.indexOf(directPlanet);
    if (idx !== -1) {
      setColor(OVERLAY_COLORS[idx % OVERLAY_COLORS.length]);
    }
  }, [directPlanet]);


  const handleApiCall = async (url, payload, studyLabel, processOverlays) => {
    // Log the service call before making the request
    console.log(`[AreaHighlightDialog] Calling service:`, { url, payload, studyLabel });
    // The frame as it goes on the wire, on its own line. The object above is easy to skim past,
    // and this is the one value worth being able to read at a glance against what the dropdown
    // shows and against what the server logs it parsed.
    console.log('[AreaHighlight API Payload]', { url, zodiac: payload.zodiac, planet: payload.planet, sign: payload.sign });
    try {
      const response = await api.post(url, payload);
      const data = response.data;
      console.log('[DEBUG] Full backend response:', JSON.stringify(data, null, 2));
      console.log('[AreaHighlightDialog] Backend response:', data);
      // Expect: {datePairs: [...], summaryLines: [...], futureDates: [...]}
      console.log('[Diagnostics] Received full data:', data);
      if (data) {
        processOverlays(data, studyLabel);
      } else {
        console.log(`Study '${studyLabel}' returned no data.`);
      }
    } catch (error) {
      console.error(`Failed to run study '${studyLabel}':`, error);
    }
  };

  /**
   * The frame a run is using, pinned for the whole run.
   *
   * Everything downstream — the payload, the log line, and the overlay's params — reads this
   * local rather than the state variable. The overlay is built in an async callback that lands
   * after the request returns, so reading state there reports whatever the frame happens to be by
   * then, not the frame the study was actually computed in. That is how an overlay came to be
   * labelled geo-sidereal for a run that went out as helio.
   */
  const handleDegreeRangeRun = async () => {
    const frame = zodiacType1;
    console.log('[AreaHighlight RUN] Selected frame before dispatch:', frame);
    setLastRunStudy('degreeRange');
    await logActivity(api, user, 'RUN_STUDY', {
      studyType: 'planetInDegreeRange',
      parameters: { planet: planetFrom, fromDegree, toDegree, zodiac: frame }
    });
    console.log('[AreaHighlightDialog] handleDegreeRangeRun triggered', { planetFrom, fromDegree, toDegree, frame, barData });
    // Tagged with the frame it ran in, so two bands from different frames are told apart
    // on the chart. `frame` is pinned above, so this cannot drift from what was sent.
    const studyLabel = `${withFrame(planetFrom, frame)} ${fromDegree}-${toDegree}`;
    const payload = {
      planet: planetFrom,
      fromDegree: fromDegree,
      toDegree: toDegree,
      zodiac: frame,
      startDateTime: barData.dates[0],
      endDateTime: barData.dates[barData.dates.length - 1],
      symbol: barData.symbol || undefined,
    };
    const url = '/api/area-highlight-study/runAreaHighlightStudy';
    handleApiCall(url, payload, studyLabel, (data) => {
      addAreaHighlightOverlaysAndMessages({
        isAdmin,
        datePairs: data.datePairs || [],
        summaryLines: data.summaryLines || [],
        futureDates: data.futureDates || [],
        params: {
          color,
          label: studyLabel,
          planet1: planetFrom,
          zodiac: frame,
          fromDegree,
          toDegree
        },
        addOverlay,
        barData,
        extendedBarData,
        updateMessagesPanel: typeof onRun === 'function' ? () => {
          onRun(data);
        } : undefined,
        onJournalAppend,
      });
    });
  };

  const handlePlanetInSignRun = async () => {
    const frame = planetSignZodiac;
    console.log('[AreaHighlight RUN] Selected frame before dispatch:', frame);
    setLastRunStudy('planetInSign');
    await logActivity(api, user, 'RUN_STUDY', {
      studyType: 'planetInSign',
      parameters: { planet: planetSignPlanet, sign: planetSignSign, zodiac: frame }
    });
    console.log('[AreaHighlightDialog] handlePlanetInSignRun triggered', { planetSignPlanet, planetSignSign, frame, barData, extendedBarData });
    const payload = {
      planet: planetSignPlanet,
      sign: SIGNS.indexOf(planetSignSign),
      zodiac: frame,
      startDateTime: barData.dates[0],
      endDateTime: barData.dates[barData.dates.length - 1],
      symbol: barData.symbol || undefined,
    };
    const dynamicLabel = `${withFrame(planetSignPlanet, frame)} in ${planetSignSign}`;
    handleApiCall(
      '/api/area-highlight-study/planet-in-sign',
      payload,
      dynamicLabel,
      (data) => {
        const { datePairs, summaryLines, futureDates } = data;
        console.log('[AreaHighlightDialog] [Planet in Sign] Passing to overlays/messages:', { datePairs, summaryLines, futureDates });
        addAreaHighlightOverlaysAndMessages({
        isAdmin,
          datePairs: datePairs || [],
          summaryLines: summaryLines || [],
          futureDates: futureDates || [],
          params: {
            color,
            label: dynamicLabel,
            planet1: planetSignPlanet,
            sign: planetSignSign,
            zodiac: frame,
          },
          addOverlay,
          barData,
          extendedBarData,
          updateMessagesPanel: typeof onRun === 'function' ? () => {
            onRun(data);
          } : undefined,
          onJournalAppend,
        });
      }
    );
  };

  const handlePlanetInNakshatraRun = async () => {
    setLastRunStudy('planetInNakshatra');
    await logActivity(api, user, 'RUN_STUDY', {
      studyType: 'planetInNakshatra',
      parameters: { planet: planetNakshatraPlanet, nakshatra: planetNakshatra }
    });
    console.log('[AreaHighlightDialog] handlePlanetInNakshatraRun triggered', { planetNakshatraPlanet, planetNakshatra, barData });
    const payload = {
      planet: planetNakshatraPlanet,
      nakshatra: planetNakshatra,
      startDateTime: barData.dates[0],
      endDateTime: barData.dates[barData.dates.length - 1],
      symbol: barData.symbol,
    };
    handleApiCall(
      '/api/area-highlight-study/planet-in-nakshatra',
      payload,
      `Planet in Nakshatra: ${planetNakshatraPlanet} in ${planetNakshatra}`,
      (data) => {
        const { datePairs, summaryLines, futureDates } = data;
        console.log('[AreaHighlightDialog] [Nakshatra] Passing to overlays/messages:', { datePairs, summaryLines, futureDates });
        addAreaHighlightOverlaysAndMessages({
        isAdmin,
          datePairs: datePairs || [],
          summaryLines: summaryLines || [],
          futureDates: futureDates || [],
          params: {
            color,
            label: `Planet in Nakshatra: ${planetNakshatraPlanet} in ${planetNakshatra}`,
            planet1: planetNakshatraPlanet,
            nakshatra: planetNakshatra,
            extendedDates: extendedBarData?.dates,
            realBarEndDate: barData.dates[barData.dates.length - 1],
          },
          addOverlay,
          barData,
          extendedBarData,
          updateMessagesPanel: typeof onRun === 'function' ? () => {
            onRun(data);
          } : undefined,
          onJournalAppend,
        });
      }
    );
  };

  const handlePlanetInNakshatraCharanRun = async () => {
    setLastRunStudy('planetInNakshatraCharan');
    await logActivity(api, user, 'RUN_STUDY', {
      studyType: 'planetInNakshatraCharan',
      parameters: { planet: planetCharanPlanet, nakshatra: planetCharanNakshatra, charan: planetCharanCharan }
    });
    console.log('[AreaHighlightDialog] handlePlanetInNakshatraCharanRun triggered', { planetCharanPlanet, planetCharanNakshatra, planetCharanCharan, barData });
    const payload = {
      planet: planetCharanPlanet,
      nakshatra: planetCharanNakshatra,
      charan: planetCharanCharan,
      startDateTime: barData.dates[0],
      endDateTime: barData.dates[barData.dates.length - 1],
      symbol: barData.symbol,
    };
    const dynamicLabel = `Planet in Nakshatra Charan: ${planetCharanPlanet} in ${planetCharanNakshatra} Charan ${planetCharanCharan}`;
    handleApiCall(
      '/api/area-highlight-study/planet-in-nakshatra-charan',
      payload,
      dynamicLabel,
      (data) => {
        const { datePairs, summaryLines, futureDates } = data;
        console.log('[AreaHighlightDialog] [NakshatraCharan] Passing to overlays/messages:', { datePairs, summaryLines, futureDates });
        addAreaHighlightOverlaysAndMessages({
        isAdmin,
          datePairs: datePairs || [],
          summaryLines: summaryLines || [],
          futureDates: futureDates || [],
          params: {
            color,
            label: dynamicLabel,
            planet1: planetCharanPlanet,
            nakshatra: planetCharanNakshatra,
            charan: planetCharanCharan,
            extendedDates: extendedBarData?.dates,
            realBarEndDate: barData.dates[barData.dates.length - 1],
          },
          addOverlay,
          barData,
          extendedBarData,
          updateMessagesPanel: typeof onRun === 'function' ? () => {
            onRun(data);
          } : undefined,
          onJournalAppend,
        });
      }
    );
  };

  const handleRetroDirectRun = async (isRetrograde) => {
    setLastRunStudy(isRetrograde ? 'retroToDirect' : 'directToRetro');
    const planetToUse = isRetrograde ? retroPlanet : directPlanet;
    await logActivity(api, user, 'RUN_STUDY', {
      studyType: isRetrograde ? 'retrogradeToDirect' : 'directToRetrograde',
      parameters: { planet: planetToUse }
    });
    console.log('[AreaHighlightDialog] handleRetroDirectRun triggered', { retroPlanet, directPlanet, barData, isRetrograde });
  // Abbreviated because this string is drawn onto the chart as the area-highlight label, where
  // "Venus Retrograde to Direct" is wide enough to collide with the next study's label. The
  // dialog's own field headings stay spelled out — a form label has room and needs to be plain.
  const studyLabel = retrogradeStudyLabel(planetToUse, isRetrograde);
  const payload = {
    planet: planetToUse,
    startDateTime: barData.dates[0],
    endDateTime: barData.dates[barData.dates.length - 1],
    symbol: barData.symbol || undefined,
  };
  handleApiCall(
    isRetrograde ? '/api/area-highlight-study/planet-retro-direct' : '/api/area-highlight-study/planet-direct-retro',
    payload,
    studyLabel,
    (data) => {
      addAreaHighlightOverlaysAndMessages({
        isAdmin,
        datePairs: data.datePairs || [],
        summaryLines: data.summaryLines || [],
        futureDates: data.futureDates || [],
        params: {
          color,
          label: studyLabel,
          planet1: planetToUse,
          retroType: isRetrograde ? 'retro-direct' : 'direct-retro',
        },
        addOverlay,
        barData,
        extendedBarData,
        updateMessagesPanel: typeof onRun === 'function' ? () => {
          onRun(data);
        } : undefined,
        onJournalAppend,
      });
    }
  );
};


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" sx={{ height: '80vh' }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Area Highlight Studies</span>
        <HelpIcon helpKey="areaHighlightDialog" />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {/* Color Picker */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography sx={{ fontWeight: 'bold' }}>Select Overlay Color:</Typography>
            <Select
              value={color}
              onChange={(e) => setColor(e.target.value)}
              size="small"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, backgroundColor: selected, border: '1px solid #ccc', borderRadius: '4px' }} />
                  {selected}
                </Box>
              )}
              inputProps={{ id: 'area-highlight-color-select' }}
            >
              {OVERLAY_COLORS.map(c => (
                <MenuItem key={c} value={c}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, backgroundColor: c, border: '1px solid #ccc', borderRadius: '4px' }} />
                    {c}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Planet in Degree Range */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: lastRunStudy === 'degreeRange' ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 1, transition: 'border 0.3s' }}>
            <Typography sx={{ minWidth: 200, fontWeight: 'bold' }}>Planet in Degree Range:</Typography>
            <Select value={planetFrom} onChange={(e) => setPlanetFrom(e.target.value)} size="small">{planetsForFrame(zodiacType1).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select>
            <TextField label="From" value={fromDegree} onChange={(e) => setFromDegree(e.target.value)} size="small" sx={{ width: 80 }} />
            <TextField label="To" value={toDegree} onChange={(e) => setToDegree(e.target.value)} size="small" sx={{ width: 80 }} />
            <Select value={zodiacType1} onChange={(e) => setZodiacType1(e.target.value)} size="small" inputProps={{ 'aria-label': 'Frame for Planet in Degree Range' }}>{zodiacTypesFor(adminAllowed, zodiacType1).map(z => <MenuItem key={z} value={z}>{zodiacLabel(z)}</MenuItem>)}</Select>
            <Button onClick={handleDegreeRangeRun} variant="contained" size="small" sx={{ ml: 'auto' }}>Run</Button>
          </Box>

          {/* Planet in Sign */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: lastRunStudy === 'planetInSign' ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 1, transition: 'border 0.3s' }}>
            <Typography sx={{ minWidth: 200, fontWeight: 'bold' }}>Planet in Sign:</Typography>
            <Select value={planetSignPlanet} onChange={(e) => setPlanetSignPlanet(e.target.value)} size="small">{planetsForFrame(planetSignZodiac).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select>
            <Select value={planetSignSign} onChange={(e) => {
  const idx = SIGNS.indexOf(e.target.value);
  setPlanetSignSign(e.target.value);
  setColor(OVERLAY_COLORS[idx % OVERLAY_COLORS.length]);
}} size="small">{SIGNS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select>
            <Select value={planetSignZodiac} onChange={(e) => setPlanetSignZodiac(e.target.value)} size="small" inputProps={{ 'aria-label': 'Frame for Planet in Sign' }}>{zodiacTypesFor(adminAllowed, planetSignZodiac).map(z => <MenuItem key={z} value={z}>{zodiacLabel(z)}</MenuItem>)}</Select>
            <Button onClick={handlePlanetInSignRun} variant="contained" size="small" sx={{ ml: 'auto' }}>Run</Button>
          </Box>

          {/* Planet in Nakshatra */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: lastRunStudy === 'planetInNakshatra' ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 1, transition: 'border 0.3s' }}>
            <Typography sx={{ minWidth: 200, fontWeight: 'bold' }}>Planet in Nakshatra:</Typography>
            <Select value={planetNakshatraPlanet} onChange={(e) => setPlanetNakshatraPlanet(e.target.value)} size="small">{PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select>
            <Select value={planetNakshatra} onChange={(e) => setPlanetNakshatra(e.target.value)} size="small">{NAKSHATRAS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}</Select>
            <Button onClick={handlePlanetInNakshatraRun} variant="contained" size="small" sx={{ ml: 'auto' }}>Run</Button>
          </Box>

          {/* Planet in Nakshatra Charan */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: lastRunStudy === 'planetInNakshatraCharan' ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 1, transition: 'border 0.3s' }}>
            <Typography sx={{ minWidth: 200, fontWeight: 'bold' }}>Planet in Nakshatra Charan:</Typography>
            <Select value={planetCharanPlanet} onChange={(e) => setPlanetCharanPlanet(e.target.value)} size="small">{PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select>
            <Select value={planetCharanNakshatra} onChange={(e) => setPlanetCharanNakshatra(e.target.value)} size="small">{NAKSHATRAS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}</Select>
            <Select value={planetCharanCharan} onChange={(e) => setPlanetCharanCharan(e.target.value)} size="small">{CHARANS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}</Select>
            <Button
              onClick={handlePlanetInNakshatraCharanRun}
              variant="contained"
              size="small"
              sx={{ ml: 'auto' }}
            >
              Run
            </Button>
          </Box>

          {/* Retrograde to Direct */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: lastRunStudy === 'retroToDirect' ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 1, transition: 'border 0.3s' }}>
            <Typography sx={{ minWidth: 200, fontWeight: 'bold' }}>Retrograde to Direct:</Typography>
            <Select value={retroPlanet} onChange={(e) => setRetroPlanet(e.target.value)} size="small">{RETRO_PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select>
            <Button onClick={() => handleRetroDirectRun(true)} variant="contained" size="small" sx={{ ml: 'auto' }}>Run</Button>
          </Box>

          {/* Direct to Retrograde */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: lastRunStudy === 'directToRetro' ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 1, transition: 'border 0.3s' }}>
            <Typography sx={{ minWidth: 200, fontWeight: 'bold' }}>Direct to Retrograde:</Typography>
            <Select value={directPlanet} onChange={(e) => setDirectPlanet(e.target.value)} size="small">{RETRO_PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select>
            <Button onClick={() => handleRetroDirectRun(false)} variant="contained" size="small" sx={{ ml: 'auto' }}>Run</Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="error">Close</Button>
      </DialogActions>
    </Dialog>
  );
};
AreaHighlightDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  barData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string).isRequired,
    ohlc: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
    symbol: PropTypes.string,
  }).isRequired,
  extendedBarData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string),
    ohlc: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  }),
  onRun: PropTypes.func,
  onJournalAppend: PropTypes.func,
};

export default AreaHighlightDialog;